import { Box } from "@mui/material";
import { type FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import type { OutreachPlanChoice, RequestOutreachActions } from "../../../hooks/useRequestOutreach";
import { OUTREACH_POPUP } from "../../../theme/tokens";
import { type OutreachAllowed, OutreachStatus, type RequestOutreach } from "../../../utils/models";
import SystemBanner from "../../UIToolkit/molecules/SystemBanner";
import { MultiSizePopup } from "../../UIToolkit/Popups/MultiSizePopup/MultiSizePopup";
import { MultiSizePopupState } from "../../UIToolkit/Popups/MultiSizePopup/utils";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { OutreachAdjustPanel } from "./OutreachAdjustPanel";
import { OutreachApprovalCallout } from "./OutreachApprovalCallout";
import { OutreachEffortPanel } from "./OutreachEffortPanel";
import { OutreachPeopleList } from "./OutreachPeopleList";
import { OutreachShiftFacts } from "./OutreachShiftFacts";
import { isCalling, OutreachRowAction, OutreachTab } from "./outreachGroupTypes";
import { type OutreachCopy, useOutreachCopy } from "./useOutreachCopy";
import { useOutreachGroups } from "./useOutreachGroups";

type Props = {
	open: boolean;
	onClose: () => void;
	onReview: () => void;
	outreach: RequestOutreach;
	actions: RequestOutreachActions;
};

/** The "Who we're asking" Fill Shift popup: the shift, how hard outreach pushes, who accepted, and who heard from us, who's next and who's left out. */
export const RequestOutreachPopup: FC<Props> = ({ open, onClose, onReview, outreach, actions }) => {
	const { t } = useTranslation();
	return (
		<MultiSizePopup
			open={open}
			title={t("request.outreach.title")}
			onClose={onClose}
			disableMinimize
			closeOnClickOutside
			closeOnEscape
			data-testid="outreach-popup"
			bodyRenderer={(state) => (state === MultiSizePopupState.MAXIMIZED ? <OutreachBody outreach={outreach} actions={actions} onReview={onReview} /> : null)}
		/>
	);
};

const ALLOWED_OFF: readonly (keyof OutreachAllowed)[] = ["calls", "overtime", "agency"];

/** "Level 4 (our pick: Level 2). Holding. No calls · No OT." — what the user changed, in one line. */
const adjustedDetailOf = (t: ReturnType<typeof useTranslation>["t"], copy: OutreachCopy, outreach: RequestOutreach): string => {
	const { effort } = outreach;
	const parts = [
		...(effort.setByUser
			? [t("request.outreach.banner.adjusted_level", { level: copy.levelNumber(effort.level), pick: copy.levelNumber(effort.automaticLevel) })]
			: []),
		...(effort.holding ? [t("request.outreach.banner.holding")] : []),
	];
	const off = ALLOWED_OFF.filter((key) => !effort.allowed[key]).map((key) => t(`request.outreach.banner.off.${key}`));
	return [...parts, ...(off.length > 0 ? [`${off.join(" · ")}.`] : [])].join(" ");
};

const OutreachBody: FC<{ outreach: RequestOutreach; actions: RequestOutreachActions; onReview: () => void }> = ({ outreach, actions, onReview }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	// The same moment the outreach was worked out for, so captions and lists never disagree with its level and status.
	const now = outreach.asOf;
	const copy = useOutreachCopy();
	const groups = useOutreachGroups(outreach, copy, now);
	const [tab, setTab] = useState(OutreachTab.Reached);
	const [panelOpen, setPanelOpen] = useState(false);
	const paused = outreach.status === OutreachStatus.Paused;
	const { effort } = outreach;
	const adjusted = effort.setByUser || ALLOWED_OFF.some((key) => !effort.allowed[key]);
	const base = { request_id: outreach.requestId, level: effort.level };

	useEffect(() => {
		track("outreach_popup_viewed", { request_id: outreach.requestId });
	}, [track, outreach.requestId]);

	const togglePause = useCallback(() => {
		track(paused ? "outreach_resumed" : "outreach_paused", { request_id: outreach.requestId });
		if (paused) actions.resume();
		else actions.pause();
	}, [paused, actions, track, outreach.requestId]);

	const onAction = (action: OutreachRowAction, nurseId: number) => {
		track("outreach_person_action", { ...base, action, nurse_id: nurseId });
		switch (action) {
			case OutreachRowAction.SendNow:
				actions.textNow(nurseId);
				break;
			case OutreachRowAction.CallNow:
				actions.callNow(nurseId);
				break;
			// An overtime offer ahead of its level is a Text anyway past the daily-overtime filter.
			case OutreachRowAction.OfferNow:
			case OutreachRowAction.TextAnyway:
				actions.textAnyway(nurseId);
				break;
			case OutreachRowAction.Skip:
				actions.skip(nurseId);
				break;
			case OutreachRowAction.Undo:
				actions.undoSkip(nurseId);
				break;
			default: {
				const unhandled: never = action;
				throw new Error(`Outreach: row action ${String(unhandled)} has no handler`);
			}
		}
	};

	const apply = (choice: OutreachPlanChoice) => {
		track("outreach_plan_adjusted", {
			...base,
			to_level: choice.level,
			holding: choice.holding,
			calls: choice.allowed.calls,
			overtime: choice.allowed.overtime,
			agency: choice.allowed.agency,
		});
		actions.adjustPlan(choice);
		setPanelOpen(false);
		if (choice.level !== effort.level) setTab(OutreachTab.Reached);
	};

	return (
		// One scroll for the whole body: the panels can be taller than the popup, and a list squeezed under them would vanish.
		// No top padding here — sticky tabs pin below a scroll container's padding, leaving a gap; the first child carries it.
		<Box
			data-testid="outreach-popup-body"
			sx={{ width: OUTREACH_POPUP.WIDTH, height: OUTREACH_POPUP.HEIGHT, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1.5, px: 3 }}
		>
			<Box sx={{ pt: 2.25, flexShrink: 0 }}>
				<OutreachShiftFacts outreach={outreach} />
			</Box>
			<OutreachEffortPanel outreach={outreach} copy={copy} now={now} panelOpen={panelOpen} onTogglePause={togglePause} onOpenPanel={() => setPanelOpen(true)} />
			{panelOpen && <OutreachAdjustPanel outreach={outreach} copy={copy} onApply={apply} onCancel={() => setPanelOpen(false)} />}
			{paused ? (
				<SystemBanner
					live
					tone="warning"
					layout="inline"
					dataTestId="outreach-paused-banner"
					title={t("request.outreach.banner.paused_title")}
					description={t("request.outreach.banner.paused_detail")}
				/>
			) : (
				adjusted && (
					<SystemBanner
						live
						tone="primary"
						layout="inline"
						dataTestId="outreach-mode-banner"
						title={t("request.outreach.banner.adjusted_title")}
						description={adjustedDetailOf(t, copy, outreach)}
						action={
							<Box sx={{ display: "flex", gap: 0.5 }}>
								<SystemButton data-testid="outreach-mode-change" buttonVariant="borderless" size="small" noFullWidthMobile onClick={() => setPanelOpen(true)}>
									{t("request.outreach.banner.change")}
								</SystemButton>
								<SystemButton
									data-testid="outreach-mode-reset"
									buttonVariant="borderless"
									size="small"
									noFullWidthMobile
									onClick={() => {
										track("outreach_plan_reset", base);
										actions.resetPlan();
									}}
								>
									{t("request.outreach.banner.back_to_plan")}
								</SystemButton>
							</Box>
						}
					/>
				)
			)}
			<OutreachApprovalCallout
				outreach={outreach}
				now={now}
				onReview={() => {
					track("outreach_pickups_review_opened", base);
					onReview();
				}}
			/>
			<OutreachPeopleList
				groups={groups}
				tab={tab}
				onTabChange={setTab}
				copy={copy}
				calling={isCalling(outreach)}
				// Sends only make sense while outreach is still asking; filled, finished and canceled are as final as paused.
				sendsDisabled={outreach.status !== OutreachStatus.Running}
				onAction={onAction}
			/>
		</Box>
	);
};
